import { createContext } from "react";
import openSocket from "socket.io-client";
import { isExpired } from "react-jwt";
import { getBackendOrigin } from "../../utils/runtimeUrls";

class ManagedSocket {
  constructor(socketManager) {
    this.socketManager = socketManager;
    this.rawSocket = socketManager.currentSocket;
    this.callbacks = [];
    this.joins = [];

    this._onConnectHandler = () => {
      // Reatachar todos os callbacks registrados ao reconectar
      for (const j of this.callbacks) {
        this.rawSocket.off(j.event, j.callback);
        this.rawSocket.on(j.event, j.callback);
      }

      // Re-emitir todos os canais de fila e notificações (joins)
      const refreshJoins = () => {
        for (const j of this.joins) {
          console.debug("Re-emitindo inscrição no canal após conexão/ready:", j);
          this.rawSocket.emit(`join${j.event}`, ...j.params);
        }
      };

      if (this.socketManager.socketReady && this.rawSocket.connected) {
        refreshJoins();
      } else {
        this.rawSocket.once("ready", refreshJoins);
      }
    };

    this.rawSocket.on("connect", this._onConnectHandler);
  }

  on(event, callback) {
    if (event === "ready" || event === "connect") {
      return this.socketManager.onReady(callback);
    }
    this.callbacks.push({ event, callback });
    return this.rawSocket.on(event, callback);
  }

  off(event, callback) {
    const i = this.callbacks.findIndex(
      c => c.event === event && c.callback === callback
    );
    if (i !== -1) {
      this.callbacks.splice(i, 1);
    }
    return this.rawSocket.off(event, callback);
  }

  emit(event, ...params) {
    if (event.startsWith("join")) {
      const joinName = event.substring(4);
      // Evitar joins duplicados com os mesmos parâmetros
      this.joins = this.joins.filter(
        j => !(j.event === joinName && JSON.stringify(j.params) === JSON.stringify(params))
      );
      this.joins.push({ event: joinName, params });
    }
    return this.rawSocket.emit(event, ...params);
  }

  disconnect() {
    for (const j of this.joins) {
      this.rawSocket.emit(`leave${j.event}`, ...j.params);
    }
    this.joins = [];
    for (const c of this.callbacks) {
      this.rawSocket.off(c.event, c.callback);
    }
    this.callbacks = [];
    if (this._onConnectHandler) {
      this.rawSocket.off("connect", this._onConnectHandler);
      this._onConnectHandler = null;
    }
  }
}

class DummySocket {
  on(..._) {}
  off(..._) {}
  emit(..._) {}
  disconnect() {}
}

const SocketManager = {
  currentCompanyId: -1,
  currentUserId: -1,
  currentSocket: null,
  socketReady: false,
  _visibilityListenerAttached: false,

  getSocket: function (companyId) {
    let userId = null;
    if (localStorage.getItem("userId")) {
      userId = localStorage.getItem("userId");
    }

    if (!companyId && !this.currentSocket) {
      return new DummySocket();
    }

    if (companyId && typeof companyId !== "string") {
      companyId = `${companyId}`;
    }

    if (companyId !== this.currentCompanyId || userId !== this.currentUserId || !this.currentSocket) {
      if (this.currentSocket) {
        console.warn("Fechando socket antigo - empresa ou usuário alterado");
        this.currentSocket.removeAllListeners();
        this.currentSocket.disconnect();
        this.currentSocket = null;
        this.currentCompanyId = null;
        this.currentUserId = null;
        this.socketReady = false;
      }

      let token = JSON.parse(localStorage.getItem("token"));
      if (!token) {
        return new DummySocket();
      }

      if (isExpired(token)) {
        console.warn("Token expirado, recarregando...");
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return new DummySocket();
      }

      this.currentCompanyId = companyId;
      this.currentUserId = userId;
      this.socketReady = false;

      const backendUrl = getBackendOrigin();

      this.currentSocket = openSocket(backendUrl, {
        transports: ["websocket", "polling"],
        timeout: 30000,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        query: { token }
      });

      this.currentSocket.io.on("reconnect_attempt", () => {
        this.currentSocket.io.opts.query.r = 1;
        token = JSON.parse(localStorage.getItem("token"));
        if (isExpired(token)) {
          console.warn("Token expirado na tentativa de reconexão");
          window.location.reload();
        } else {
          this.currentSocket.io.opts.query.token = token;
        }
      });

      this.currentSocket.on("disconnect", reason => {
        console.warn(`Socket desconectado: ${reason}`);
        this.socketReady = false;

        if (reason.startsWith("io server disconnect") || reason === "transport close") {
          token = JSON.parse(localStorage.getItem("token"));
          if (isExpired(token)) {
            window.location.reload();
            return;
          }
          this.currentSocket.io.opts.query.token = token;
          this.currentSocket.io.opts.query.r = 1;
          setTimeout(() => {
            if (this.currentSocket && !this.currentSocket.connected) {
              this.currentSocket.connect();
            }
          }, 1000);
        }
      });

      this.currentSocket.on("connect", () => {
        console.warn("Socket conectado ao servidor");
      });

      this.currentSocket.on("ready", () => {
        this.socketReady = true;
      });

      // Garantir reconexão instantânea caso a aba volte a ficar visível/focada
      if (!this._visibilityListenerAttached && typeof document !== "undefined") {
        this._visibilityListenerAttached = true;
        const handleVisibilityChange = () => {
          if (document.visibilityState === "visible") {
            if (this.currentSocket && !this.currentSocket.connected) {
              console.warn("Aba voltou a ficar visível: reconectando socket imediatamente...");
              this.currentSocket.connect();
            }
          }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("focus", handleVisibilityChange);
      }
    }

    return new ManagedSocket(this);
  },

  onReady: function (callbackReady) {
    if (this.socketReady && this.currentSocket?.connected) {
      callbackReady();
      return;
    }

    if (this.currentSocket) {
      this.currentSocket.once("ready", () => {
        callbackReady();
      });
    }
  },

  onConnect: function (callbackReady) {
    this.onReady(callbackReady);
  }
};

const SocketContext = createContext();

export { SocketContext, SocketManager };
